/**
 * Meus planos (spec 004, 7; spec 007, UC-01, UC-07, UC-21; spec 038, secao 3): cada matricula com status, etapa, a
 * conta na plataforma em destaque, prazo do teste, a mensalidade numa linha e o proximo passo; agendar e trocar a data
 * de inicio; pedir a analise do resultado. "Etapas" mostra a jornada (o inicio de cada etapa e o que falta) e
 * "Agendamento" os planos que esperam o inicio.
 */
import {
  Aviso, BotaoLink, CabecalhoDePagina, Carregando, CartaoDePlano, Grade, IconeVoltar, LinkInterno, Percurso, Pilha, ProximoPasso, RegulamentoDaCompra, Secao, Selo, Vazio,
  formatarData, formatarDia, hojeEmSaoPaulo, mensagemDe, type DadoDaConta, type DocumentoAceito, type Fato, type PassoDoPercurso,
} from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useApi } from "../acoes";
import { nomeDaCobranca, valorDaParcela } from "../cobranca";
import { paginaDoRegulamento } from "../regulamento";
import { ContaNaCorretoraDoCliente, contaDoPlanoDaGlobal, etapaDaGlobal } from "../corretora";
import { useDentroDeAssunto } from "../assunto";
import { usePassoDoPlano } from "../passo";
import { useFiltroDePlano } from "../planoAtual";
import { useSessao } from "../sessao";
import { useVisao } from "../visao";
import { ETAPA_CURTA, STATUS_DA_MATRICULA, type Matricula, type Solicitacao } from "../tipos";

/** Beneficio temporario (Vitor, 16/09): ate quando vale, ou por quantos dias depois da liberacao. Permanente: nada. */
function prazoDoBeneficio(b: { duracao_dias: number | null; vale_ate: string | null }): string | null {
  if (b.vale_ate) return b.vale_ate < hojeEmSaoPaulo() ? `Venceu em ${formatarDia(b.vale_ate)}` : `Vale até ${formatarDia(b.vale_ate)}`;
  return b.duracao_dias ? `Vale ${b.duracao_dias} dias a partir da liberação` : null;
}

export function useMatriculas() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["matriculas"], queryFn: () => ler<{ itens: Matricula[] }>("/v1/me/matriculas") });
}
export function useSolicitacoes() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["solicitacoes"], queryFn: () => ler<{ itens: Solicitacao[] }>("/v1/me/solicitacoes") });
}

type RegulamentoDoPlanoApi = { versao: { numero: number; rotulo: string | null; publicada_em: string } | null; documentos: (Omit<DocumentoAceito, "href"> & { url: string })[]; sem_aceite: string | null };

/**
 * "Regulamento desta compra" (spec 042, 3.6): a versao que ESTE plano aceitou na compra, a hora do aceite e o arquivo de
 * cada documento daquela versao. E ele que vale pra este plano ate o fim, mesmo que saia versao nova (017, decisao 6).
 */
function RegulamentoDoPlano({ matriculaId }: { matriculaId: string }) {
  const { ler } = useApi();
  const q = useQuery({ queryKey: ["regulamento-do-plano", matriculaId], queryFn: () => ler<RegulamentoDoPlanoApi>(`/v1/me/matriculas/${matriculaId}/regulamento`), staleTime: 5 * 60_000 });
  return (
    <RegulamentoDaCompra
      carregando={q.isLoading}
      erro={q.isError ? mensagemDe(q.error, "Não conseguimos buscar o regulamento agora. Recarregue a página.") : null}
      regulamento={q.data ? { versao: q.data.versao, sem_aceite: q.data.sem_aceite, documentos: q.data.documentos.map(({ url, ...d }) => ({ ...d, href: paginaDoRegulamento(url) })) } : null}
    />
  );
}

type Modo = "todos" | "agendamento" | "etapas";
const TEXTOS: Record<Modo, { titulo: string; descricao: string; vazio: string }> = {
  todos: { titulo: "Meus planos", descricao: "Cada plano com a etapa, o prazo do teste, a sua conta na plataforma e o próximo passo.", vazio: "Quando você comprar um plano, ele aparece aqui." },
  agendamento: { titulo: "Agendamento", descricao: "Escolha o dia de início de cada plano. A equipe libera sua conta nessa data.", vazio: "Nenhum plano aguardando início. Os planos já liberados estão na aba Situação." },
  etapas: { titulo: "Etapas", descricao: "Onde cada plano está e o que falta pra próxima etapa. Quando bater a meta, peça a análise do resultado.", vazio: "Nenhum plano liberado ainda. A etapa começa quando a equipe libera sua conta." },
};

export function PaginaPlanos({ modo = "todos" }: { modo?: Modo }) {
  const { id } = useParams();
  const matriculas = useMatriculas();
  const solicitacoes = useSolicitacoes();
  const [aviso, setAviso] = useState<string>();
  const t = TEXTOS[modo];
  const filtro = useFiltroDePlano();
  const dentroDeAssunto = useDentroDeAssunto();
  useEffect(() => {
    if (id) document.getElementById(`plano-${id}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [id, matriculas.data]);
  if (matriculas.isLoading) return <Carregando compacto texto="Buscando seus planos" />;
  if (matriculas.isError) return <Aviso tipo="erro">{mensagemDe(matriculas.error, "Não conseguimos carregar seus planos agora. Recarregue a página.")}</Aviso>;
  const todas = matriculas.data?.itens ?? [];
  // Com mais de um plano, a tela segue o plano atual do seletor (spec 011, secao 2); "ver todos" tira o filtro.
  const lista = todas
    .filter((m) => (id ? m.id === id : modo === "agendamento" ? m.status === "AGUARDANDO_INICIO" : modo === "etapas" ? Boolean(m.modulos.etapas) : true))
    .filter((m) => (id || !filtro.matriculaId ? true : m.id === filtro.matriculaId));
  const pedidos = solicitacoes.data?.itens ?? [];
  return (
    <Pilha espaco={6}>
      {(id || !dentroDeAssunto) && (
        <CabecalhoDePagina
          titulo={id ? "Seu plano" : t.titulo}
          descricao={id ? undefined : t.descricao}
          voltar={id ? <LinkInterno href="/planos" className="mtc-voltar"><IconeVoltar tamanho={16} />Todos os planos</LinkInterno> : undefined}
        />
      )}
      {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}
      {!id && filtro.nome && todas.length > 1 && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Mostrando só o plano {filtro.nome}. Troque de plano no menu pra ver outro.</span>}
      {!lista.length ? (
        <Vazio titulo={id ? "Plano não encontrado" : "Nada por aqui"}>{id ? "Este plano não está na sua conta." : t.vazio}</Vazio>
      ) : modo === "etapas" && !id ? (
        <Pilha espaco={5}>
          {lista.map((m) => <JornadaDoPlano key={m.id} m={m} solicitacoes={pedidos} aoConcluir={setAviso} />)}
        </Pilha>
      ) : (
        <Grade minimo={360}>
          {lista.map((m) => <PlanoDoCliente key={m.id} m={m} solicitacoes={pedidos} destaque={m.id === id} aoConcluir={setAviso} />)}
        </Grade>
      )}
    </Pilha>
  );
}

function prazoDoTeste(m: Matricula): Fato {
  const e = m.modulos.etapas;
  // Encerrado nao tem prazo correndo: contar "dias restantes" de um plano que acabou confunde.
  if (m.status === "ENCERRADA") return { rotulo: "Prazo do teste", valor: "Não se aplica", detalhe: "Plano encerrado" };
  if (!e) return { rotulo: "Prazo do teste", valor: m.status === "AGUARDANDO_INICIO" ? "A partir do início" : "Não se aplica" };
  if (e.etapa !== "TESTE") return { rotulo: "Prazo do teste", valor: e.teste_aprovado_em ? "Aprovado" : "Não se aplica", detalhe: e.teste_aprovado_em ? `em ${formatarData(e.teste_aprovado_em)}` : undefined };
  if (e.teste_vencido) return { rotulo: "Prazo do teste", valor: "Encerrado", detalhe: e.teste_ate ? `O prazo acabou em ${formatarDia(e.teste_ate)}` : undefined };
  if (e.teste_sem_prazo || !e.teste_ate) return { rotulo: "Prazo do teste", valor: "Sem prazo", detalhe: e.prazo_motivo ?? undefined };
  const dias = e.dias_restantes_do_teste ?? 0;
  return { rotulo: "Prazo do teste", valor: `Até ${formatarDia(e.teste_ate)}`, detalhe: `${dias} ${dias === 1 ? "dia restante" : "dias restantes"}` };
}

/**
 * A mensalidade numa linha (spec 038): a proxima, com valor e vencimento, e a situacao; sem cobranca agora, o motivo
 * curto do backend. A regra inteira (quanto paga em cada etapa) mora na aba Etapas, embaixo da jornada.
 */
function mensalidadeDoPlano(m: Matricula, comLink: boolean): Fato | null {
  const r = m.modulos.mensalidade;
  if (!r || m.status === "ENCERRADA") return null;
  // O nome vem da API quando nao e a mensalidade (spec 064: "Taxa de manutenção", em dolar com o real pela cotacao).
  const nome = nomeDaCobranca(r.rotulo);
  const link = comLink ? <LinkInterno href="/mensalidades">{r.rotulo ? `Ver a ${nome.toLowerCase()}` : "Ver mensalidades"}</LinkInterno> : undefined;
  if (r.proxima) {
    const temValor = r.proxima.valor_centavos != null || r.proxima.valor_usd_centavos != null;
    const quanto = temValor ? `${valorDaParcela(r.proxima)} em ${r.proxima.vencimento_formatado}` : `Vence em ${r.proxima.vencimento_formatado}`;
    return { rotulo: nome, valor: <>{quanto} <Selo tom={r.situacao === "VENCIDA" || r.situacao === "SUSPENSA" ? "erro" : r.situacao === "EM_DIA" ? "sucesso" : r.situacao === "SEM_COBRANCA" || r.situacao === "VALOR_A_DEFINIR" ? "neutro" : "alerta"}>{r.situacao_rotulo}</Selo></>, detalhe: link };
  }
  return { rotulo: nome, valor: r.paga_mensalidade ? "Em dia" : (r.sem_cobranca_motivo ?? `Sem ${nome.toLowerCase()} agora`), detalhe: link };
}

/** A conta na plataforma em destaque (spec 038): subconta e mesa com copiar, a plataforma do lado. */
function contaDoPlano(m: Matricula): DadoDaConta[] {
  const c = m.modulos.corretora;
  return [
    c?.subconta ? { rotulo: "Subconta", valor: c.subconta, copiar: true } : { rotulo: "Subconta", valor: "Criada na liberação" },
    c?.mesa ? { rotulo: "Mesa", valor: c.mesa, detalhe: c.mesa_nome ?? undefined, copiar: true } : { rotulo: "Mesa", valor: "Definida na liberação" },
    { rotulo: "Plataforma", valor: c?.plataforma_rotulo ?? "Não informada" },
  ];
}

function PlanoDoCliente({ m, solicitacoes, destaque, aoConcluir }: { m: Matricula; solicitacoes: Solicitacao[]; destaque: boolean; aoConcluir: (aviso: string) => void }) {
  const { estado } = useSessao();
  const modulos = estado.status === "logado" ? estado.usuario.capacidades.modulos : [];
  // A pagina da cobranca do mes: a mensalidade da Prop ou a taxa de manutencao da Funded (spec 064).
  const comMensalidades = modulos.includes("mensalidade-manual") || modulos.includes("assinatura");
  // Spec 019, criterio 2: o cartao monta os fatos pelos modulos do ambiente. Sem etapas nem agendamento (Global), nada
  // de "Etapa", "Prazo do teste" ou "Inicio" dizendo "nao se aplica".
  const comEtapas = modulos.includes("etapas");
  const comInicio = comEtapas || modulos.includes("agendamento");
  // A conta da Global (spec 043): o resumo `corretora` e o do nelogica-v2, com o estado e o saldo do modulo `saldo`.
  const daGlobal = modulos.includes("nelogica-v2");
  const { passo, acoes, dialogos } = usePassoDoPlano(m, solicitacoes, aoConcluir);
  // O regulamento some quando o administrador desliga (spec 060).
  const { mostra } = useVisao();
  const e = m.modulos.etapas;
  const ag = m.modulos.agendamento;
  const lista = e?.caminho.map((x) => x.etapa);
  const mensalidade = mensalidadeDoPlano(m, comMensalidades);
  const fatos: Fato[] = [
    ...(daGlobal ? etapaDaGlobal(m) : []),
    ...(comEtapas ? [{ rotulo: "Etapa", valor: e?.etapa_rotulo ?? (m.status === "AGUARDANDO_INICIO" ? "Começa na liberação" : "Sem etapa") }, prazoDoTeste(m)] : []),
    // Liberado, o inicio e o dia da liberacao (spec 007, decisao 22); antes, a data que o trader agendou.
    ...(comInicio ? [{ rotulo: "Início", valor: e ? formatarDia(e.inicio) : ag ? ag.inicio_formatado : "A agendar", detalhe: e && ag && ag.inicio !== e.inicio ? `Agendado para ${ag.inicio_formatado}` : !e && ag ? "Agendado" : undefined }] : []),
    ...(!comInicio && m.ativada_em ? [{ rotulo: "Liberada em", valor: formatarData(m.ativada_em) }] : []),
    ...(mensalidade ? [mensalidade] : []),
  ];
  const b = m.modulos.beneficios;
  const beneficios = (b?.itens ?? []).filter((x) => x.situacao !== "CANCELADO");
  return (
    <>
      <CartaoDePlano
        id={`plano-${m.id}`}
        titulo={m.plano.nome}
        subtitulo={m.comprada_em ? `Comprado em ${formatarData(m.comprada_em)}` : undefined}
        status={STATUS_DA_MATRICULA[m.status]}
        etiquetas={e || b?.bonus_de ? <>{e && <Selo>{e.familia_rotulo}</Selo>}{b?.bonus_de && <Selo tom="info">Plano bônus</Selo>}</> : undefined}
        etapas={e && lista ? { lista: lista.map((x) => ETAPA_CURTA[x]), atual: Math.max(0, lista.indexOf(e.etapa)) } : undefined}
        conta={daGlobal ? contaDoPlanoDaGlobal(m) : contaDoPlano(m)}
        saldo={daGlobal ? <ContaNaCorretoraDoCliente m={m} /> : undefined}
        fatos={fatos}
        passo={passo}
        acoes={acoes}
        destaque={destaque}
      >
        {/* Beneficios da venda (spec 013, item 2): o que foi prometido e se ja chegou. Cancelado nao aparece pro trader. */}
        {Boolean(beneficios.length) && (
          <div style={{ display: "grid", gap: 6 }}>
            <span className="mtc-rotulo">Benefícios da compra</span>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
              {beneficios.map((x) => (
                <li key={x.id} className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap", alignItems: "center" }}>
                  <span>{x.nome}</span>
                  <Selo tom={x.situacao === "HONRADO" ? "sucesso" : x.situacao === "EM_VIGOR" ? "info" : "alerta"}>{x.efeito === "PLANO_EM_DOBRO" ? "Plano bônus na sua conta" : x.situacao === "EM_VIGOR" ? "Vale no seu plano" : x.situacao === "HONRADO" ? "Recebido" : "A receber"}</Selo>
                  {prazoDoBeneficio(x) && <span className="mtc-texto-2" style={{ fontSize: "var(--t-xs)" }}>{prazoDoBeneficio(x)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {mostra("regulamentos") && <RegulamentoDoPlano matriculaId={m.id} />}
      </CartaoDePlano>
      {dialogos}
    </>
  );
}

/**
 * A jornada de um plano (aba Etapas, spec 038): cada etapa do caminho com o dia em que comecou (vem em `inicios`), a
 * atual com o prazo do teste, a proxima com o que falta (ou a promessa do avanco aprovado) e o proximo passo com a
 * acao. Embaixo, a regra da mensalidade por etapa, com os valores da compra.
 */
function JornadaDoPlano({ m, solicitacoes, aoConcluir }: { m: Matricula; solicitacoes: Solicitacao[]; aoConcluir: (aviso: string) => void }) {
  const { passo, acoes, dialogos } = usePassoDoPlano(m, solicitacoes, aoConcluir);
  const e = m.modulos.etapas!;
  const inicioDe = new Map((e.inicios ?? []).map((x) => [x.etapa, x.dia]));
  const atual = Math.max(0, e.caminho.findIndex((x) => x.etapa === e.etapa));
  const espera = e.avanco_aguardando_liberacao;
  const prazo = prazoDoTeste(m);
  const passos: PassoDoPercurso[] = e.caminho.map((x, i) => {
    const dia = inicioDe.get(x.etapa) ?? (i === 0 ? e.inicio : null);
    const base = { chave: x.etapa, titulo: x.rotulo };
    if (i < atual) return { ...base, estado: "feito", quando: dia ? `Começou em ${formatarDia(dia)}` : undefined };
    if (i === atual) {
      const detalhe = x.etapa === "TESTE" && prazo.valor !== "Não se aplica" ? `Prazo do teste: ${prazo.valor}${prazo.detalhe ? `, ${prazo.detalhe}` : ""}` : m.status === "ENCERRADA" ? "Plano encerrado nesta etapa" : undefined;
      return { ...base, estado: m.status === "ENCERRADA" ? "feito" : "atual", quando: dia ? `Desde ${formatarDia(dia)}` : undefined, detalhe };
    }
    if (espera && espera.para === x.etapa) return { ...base, estado: "espera", quando: espera.aprovado_em ? `Aprovado em ${formatarData(espera.aprovado_em)}` : undefined, detalhe: `Conta pronta em até ${espera.prazo ?? "o prazo combinado"}` };
    if (i === atual + 1 && e.proximo_avanco?.para === x.etapa && m.status === "ATIVA") return { ...base, estado: "futuro", detalhe: `Depois da análise aprovada, a conta fica pronta em até ${e.proximo_avanco.prazo}` };
    return { ...base, estado: "futuro" };
  });
  const regra = m.modulos.mensalidade?.texto;
  return (
    <>
      <Secao rotulo={e.familia_rotulo} titulo={m.plano.nome} acoes={<Selo tom={STATUS_DA_MATRICULA[m.status].tom}>{STATUS_DA_MATRICULA[m.status].rotulo}</Selo>}>
        <Percurso rotulo={`Etapas do plano ${m.plano.nome}`} passos={passos} />
        {passo && <ProximoPasso passo={passo} acoes={acoes} />}
        {regra && m.status !== "ENCERRADA" && <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}><strong>Mensalidade:</strong> {regra}</p>}
        <div><BotaoLink href={`/planos/${m.id}`} tamanho="pequeno" variante="fantasma">Ver o plano completo</BotaoLink></div>
      </Secao>
      {dialogos}
    </>
  );
}
